/**
 * SEO Configuration for ivoliq Platform
 * Handles URL normalization, canonical mappings, and meta tag management
 */

// URL Standards (chosen defaults)
export const URL_STANDARDS = {
  protocol: 'https',
  domain: 'ivoliq.io', // non-www standard
  trailingSlash: false, // no trailing slash standard
};

// Canonical URL mappings - CRITICAL: Country pages are self-referential
export const CANONICAL_MAP = {
  // Homepage
  '/': 'https://ivoliq.io/',
  
  // Country-specific invoice generators (SELF-REFERENTIAL - NO consolidation)
  '/rechnung-erstellen-kostenlos-deutschland': 'https://ivoliq.io/rechnung-erstellen-kostenlos-deutschland',
  '/rechnung-erstellen-kostenlos-oesterreich': 'https://ivoliq.io/rechnung-erstellen-kostenlos-oesterreich',
  '/qr-rechnung-erstellen-kostenlos': 'https://ivoliq.io/qr-rechnung-erstellen-kostenlos',
  
  // Legal pages
  '/terms': 'https://ivoliq.io/terms',
  '/privacy': 'https://ivoliq.io/privacy',
  '/impressum': 'https://ivoliq.io/impressum',
  
  // Auth pages (noindex,follow but still canonical)
  '/login': 'https://ivoliq.io/login',
  '/signup': 'https://ivoliq.io/signup',
  
  // Other SEO pages
  '/buchhaltung-freelancer-deutschland': 'https://ivoliq.io/buchhaltung-freelancer-deutschland',
  '/buchhaltung-freelancer-oesterreich': 'https://ivoliq.io/buchhaltung-freelancer-oesterreich',
  '/buchhaltung-freelancer-schweiz': 'https://ivoliq.io/buchhaltung-freelancer-schweiz',
  '/steuerruecklagen-freelancer': 'https://ivoliq.io/steuerruecklagen-freelancer',
  '/steuerruecklagen-freelancer-deutschland': 'https://ivoliq.io/steuerruecklagen-freelancer-deutschland',
  '/steuerruecklagen-freelancer-oesterreich': 'https://ivoliq.io/steuerruecklagen-freelancer-oesterreich',
  '/steuerruecklagen-freelancer-schweiz': 'https://ivoliq.io/steuerruecklagen-freelancer-schweiz',
  '/rechnungsvorlage-freelancer': 'https://ivoliq.io/rechnungsvorlage-freelancer',
  '/lexoffice-alternative': 'https://ivoliq.io/lexoffice-alternative',
  '/sevdesk-alternative': 'https://ivoliq.io/sevdesk-alternative',
  '/zeiterfassung-freelancer': 'https://ivoliq.io/zeiterfassung-freelancer',
  '/steuerberater-portal': 'https://ivoliq.io/steuerberater-portal',
  '/pricing': 'https://ivoliq.io/pricing',
  '/tax-documentation': 'https://ivoliq.io/tax-documentation',
};

// Redirect matrix for URL normalization
export const REDIRECT_RULES = [
  // HTTP to HTTPS (301)
  {
    from: /^http:\/\//,
    to: 'https://',
    status: 301,
    type: 'protocol'
  },
  
  // www to non-www (301)
  {
    from: /^https:\/\/www\.ivoliq\.io/,
    to: 'https://ivoliq.io',
    status: 301,
    type: 'domain'
  },
  
  // Trailing slash removal (301)
  {
    from: /\/$/,
    to: '',
    status: 301,
    type: 'trailing_slash',
    exclude: ['/'] // Keep trailing slash on root
  },
  
  // Generic invoice generator to Deutschland (301)
  {
    from: '/rechnung-erstellen-kostenlos',
    to: '/rechnung-erstellen-kostenlos-deutschland',
    status: 301,
    type: 'country_default'
  },
  
  // Old rechnungsvorlage URLs to new rechnung-erstellen URLs (301)
  {
    from: '/rechnungsvorlage-freelancer-deutschland',
    to: '/rechnung-erstellen-kostenlos-deutschland',
    status: 301,
    type: 'url_migration'
  },
  {
    from: '/rechnungsvorlage-freelancer-oesterreich',
    to: '/rechnung-erstellen-kostenlos-oesterreich',
    status: 301,
    type: 'url_migration'
  },
  {
    from: '/rechnungsvorlage-freelancer-schweiz',
    to: '/qr-rechnung-erstellen-kostenlos',
    status: 301,
    type: 'url_migration'
  }
];

// Meta tag configurations
export const META_CONFIGS = {
  // Auth pages - noindex,follow
  '/login': {
    robots: 'noindex,follow',
    title: 'Login - ivoliq',
    description: 'Melden Sie sich bei ivoliq an - Zeit & Finanzen für Freelancer'
  },
  '/signup': {
    robots: 'noindex,follow',
    title: 'Registrierung - ivoliq',
    description: 'Registrieren Sie sich bei ivoliq - Zeit & Finanzen für Freelancer'
  },
  
  // Country-specific invoice generators - index,follow
  '/rechnung-erstellen-kostenlos-deutschland': {
    robots: 'index,follow',
    title: 'Rechnung erstellen kostenlos Deutschland – 30 Sek. | ivoliq',
    description: 'Kostenloser Rechnungsgenerator für Deutschland: Erstelle rechtssichere Rechnungen nach §14 UStG in 30 Sek. Pflichtangaben, PDF-Download, ohne Anmeldung.'
  },
  '/rechnung-erstellen-kostenlos-oesterreich': {
    robots: 'index,follow',
    title: 'Rechnung erstellen kostenlos Österreich – 30 Sek. | ivoliq',
    description: 'Kostenloser Rechnungsgenerator für Österreich: Erstelle rechtssichere Rechnungen nach §11 UStG in 30 Sek. UID-Nr., Pflichtangaben, PDF-Download, ohne Anmeldung.'
  },
  '/qr-rechnung-erstellen-kostenlos': {
    robots: 'index,follow',
    title: 'Rechnung erstellen kostenlos Schweiz + QR-Bill | ivoliq',
    description: 'Kostenloser Rechnungsgenerator für die Schweiz mit Swiss QR-Bill: Erstelle rechtssichere Rechnungen nach MWST-Gesetz in 30 Sek. IBAN-Validierung, ohne Anmeldung.'
  }
};

// Sitemap configuration - only canonical URLs
export const SITEMAP_URLS = [
  // Homepage
  {
    url: 'https://ivoliq.io/',
    lastmod: '2026-02-28',
    changefreq: 'weekly',
    priority: 1.0
  },
  
  // Country-specific invoice generators (HIGH PRIORITY)
  {
    url: 'https://ivoliq.io/rechnung-erstellen-kostenlos-deutschland',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.9
  },
  {
    url: 'https://ivoliq.io/rechnung-erstellen-kostenlos-oesterreich',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.9
  },
  {
    url: 'https://ivoliq.io/qr-rechnung-erstellen-kostenlos',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.9
  },
  
  // Other SEO pages
  {
    url: 'https://ivoliq.io/buchhaltung-freelancer-deutschland',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  {
    url: 'https://ivoliq.io/buchhaltung-freelancer-oesterreich',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  {
    url: 'https://ivoliq.io/buchhaltung-freelancer-schweiz',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  {
    url: 'https://ivoliq.io/steuerruecklagen-freelancer',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  {
    url: 'https://ivoliq.io/steuerruecklagen-freelancer-deutschland',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  {
    url: 'https://ivoliq.io/steuerruecklagen-freelancer-oesterreich',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  {
    url: 'https://ivoliq.io/steuerruecklagen-freelancer-schweiz',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  {
    url: 'https://ivoliq.io/rechnungsvorlage-freelancer',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  {
    url: 'https://ivoliq.io/lexoffice-alternative',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  {
    url: 'https://ivoliq.io/sevdesk-alternative',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  {
    url: 'https://ivoliq.io/steuerberater-portal',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.9
  },
  
  // Pricing
  {
    url: 'https://ivoliq.io/pricing',
    lastmod: '2026-02-28',
    changefreq: 'monthly',
    priority: 0.8
  },
  
  // Legal pages
  {
    url: 'https://ivoliq.io/impressum',
    lastmod: '2026-02-28',
    changefreq: 'yearly',
    priority: 0.3
  },
  {
    url: 'https://ivoliq.io/privacy',
    lastmod: '2026-02-28',
    changefreq: 'yearly',
    priority: 0.3
  },
  {
    url: 'https://ivoliq.io/terms',
    lastmod: '2026-02-28',
    changefreq: 'yearly',
    priority: 0.3
  },
  {
    url: 'https://ivoliq.io/tax-documentation',
    lastmod: '2026-02-28',
    changefreq: 'yearly',
    priority: 0.3
  }
  
  // EXCLUDED: /login, /signup (noindex pages)
  // EXCLUDED: redirect variants
  // EXCLUDED: /rechnung-erstellen-kostenlos (redirects to -deutschland)
];

// Internal linking strategy configuration
export const INTERNAL_LINKS = {
  // Homepage links to country pages
  homepage: [
    {
      url: '/rechnung-erstellen-kostenlos-deutschland',
      text: 'Rechnung erstellen Deutschland',
      description: 'Kostenlose Rechnungserstellung für Deutschland'
    },
    {
      url: '/rechnung-erstellen-kostenlos-oesterreich',
      text: 'Rechnung erstellen Österreich',
      description: 'Kostenlose Rechnungserstellung für Österreich'
    },
    {
      url: '/qr-rechnung-erstellen-kostenlos',
      text: 'Rechnung erstellen Schweiz',
      description: 'Kostenlose Rechnungserstellung für die Schweiz'
    }
  ],
  
  // Footer navigation
  footer: {
    section: 'Rechnung erstellen',
    links: [
      {
        url: '/rechnung-erstellen-kostenlos-deutschland',
        text: 'Deutschland'
      },
      {
        url: '/rechnung-erstellen-kostenlos-oesterreich',
        text: 'Österreich'
      },
      {
        url: '/qr-rechnung-erstellen-kostenlos',
        text: 'Schweiz'
      }
    ]
  }
};

// Utility functions
export const getCanonicalUrl = (pathname) => {
  return CANONICAL_MAP[pathname] || `https://ivoliq.io${pathname}`;
};

export const getMetaConfig = (pathname) => {
  return META_CONFIGS[pathname] || {
    robots: 'index,follow'
  };
};

export const shouldNoIndex = (pathname) => {
  const config = META_CONFIGS[pathname];
  return config && config.robots.includes('noindex');
};

export const generateSitemap = () => {
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${SITEMAP_URLS.map(item => `  <url>
    <loc>${item.url}</loc>
    <lastmod>${item.lastmod}</lastmod>
    <changefreq>${item.changefreq}</changefreq>
    <priority>${item.priority}</priority>
  </url>`).join('\n')}
</urlset>`;
  
  return xml;
};
